import Button from "@/common/components/Button";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import Modal from "@/common/components/Modal";
import { BriefcaseBusinessIcon } from "lucide-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import {
  createGovernmentAllocationDistributionSchema,
  createGovernmentAllocationDistributionQuantitySchema,
  CreateGovernmentAllocationDistributionValidationSchema,
  CreateGovernmentAllocationDistributionQuantityValidationSchema,
} from "@/features/privateCompanies/schemas/createGovernmentAllocationDistribution.schema";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import useUpdateGovernmentAllocationDistribution from "@/features/privateCompanies/hooks/useUpdateGovernmentAllocationDistribution";
import { CompanyGovernmentAllocationDistribution, ControlledByType, GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationMovementsKind } from "@/features/privateCompanies/types/companyGovernmentAllocation.type";
import GovernmentAllocationDistributionProductCategoriesForm from "./ProductCategoriesForm";
import { updateGovernmentAllocationDistributionDefaultValues } from "../../utils/updateGovernmentAllocationDistributionDefaultValues";
import { CompanyGovernmentAllocationTransformer } from "@/features/privateCompanies/services/companyGovernmentAllocationsTransformer";
import { UnitTextField } from "@/common/components/UnitTextField";
import { useParams } from "react-router-dom";
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from "@/features/privateCompanies/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts";
import { validateDistributionLimits } from "@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/utils/validateDistributionLimits";

interface GovernmentAllocationDistributionUpdateModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  distribution: CompanyGovernmentAllocationDistribution;
  controlledBy?: ControlledByType;
  movementsKind?: GovernmentAllocationMovementsKind;
}

const GovernmentAllocationDistributionUpdateModal = ({
  isOpen,
  setIsOpen,
  distribution,
  controlledBy = 'currency',
  movementsKind = 'common',
}: GovernmentAllocationDistributionUpdateModalProps) => {
  const { id: governmentAllocationId } = useParams();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const isQuantity = controlledBy === 'quantity';
  const schema = isQuantity ? createGovernmentAllocationDistributionQuantitySchema : createGovernmentAllocationDistributionSchema;

  const {
    data: allowedCategoriesAndProducts,
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId,
    filters: { companyBranchId: distribution?.branch?.id },
  });

  const {
    mutate: updateGovernmentAllocationDistribution,
    isPending: updateGovernmentAllocationDistributionIsPending,
  } = useUpdateGovernmentAllocationDistribution({
    distribution,
    controlledBy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsDistributions'] });
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsAllowedCategoriesAndProducts'] });
      queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation'] });
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: CompanyGovernmentAllocationTransformer.errorsTransformer(),
      })(error);
    },
  });

  const formMethods = useForm<CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: updateGovernmentAllocationDistributionDefaultValues(distribution, controlledBy, movementsKind),
  });

  const {
    register,
    handleSubmit,
    setError,
    watch,
    reset,
    formState: { errors },
  } = formMethods;

  const onSubmit: SubmitHandler<
    | CreateGovernmentAllocationDistributionValidationSchema
    | CreateGovernmentAllocationDistributionQuantityValidationSchema
  > = (form) => {
    if (allowedCategoriesAndProducts) {
      const isValid = validateDistributionLimits({
        form,
        allowedCategoriesAndProducts,
        controlledBy,
        setError,
      });

      if (!isValid) return;
    }

    updateGovernmentAllocationDistribution(form);
  };

  const productCategoriesDistributions = watch('product_categories_distributions');

  useEffect(() => {
    if (isOpen) {
      reset(updateGovernmentAllocationDistributionDefaultValues(distribution, controlledBy, movementsKind));
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value);

          if (!value) {
            setTimeout(reset, 500);
          }
        }}
        title="Detalhando crédito da filial"
        showClose
        className="min-w-[640px] overflow-hidden"
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <ImageWithDisplayName
                placeholderIcon={BriefcaseBusinessIcon}
                imageUrl={distribution?.branch?.general_information?.company_logo?.url}
                displayName={distribution?.branch?.display_name}
                imageClassName="size-8"
                iconClassName="size-6"
              />

              <div className="max-h-[480px] min-h-[320px] overflow-auto flex flex-col gap-6 pe-2">
                {isQuantity ? (
                  <UnitTextField
                    label="Quantidade limite"
                    decimals={2}
                    autoCorrect="off"
                    forceValue
                    {...register('total_quantity')}
                    value={watch('total_quantity')}
                    errorMessage={(errors as { total_quantity?: { message?: string } }).total_quantity?.message || undefined}
                    disabled={productCategoriesDistributions?.length > 0}
                  />
                ) : (
                  <CurrencyTextField
                    label="Valor limite"
                    {...register('total_value')}
                    errorMessage={(errors as { total_value?: { message?: string } }).total_value?.message || undefined}
                    forceValue
                    value={watch('total_value')}
                    disabled={productCategoriesDistributions?.length > 0}
                  />
                )}

                {movementsKind !== 'fee' && (
                  <GovernmentAllocationDistributionProductCategoriesForm
                    distribution={distribution}
                    companyBranchId={distribution?.branch?.id}
                    controlledBy={controlledBy}
                  />
                )}
              </div>

              <Button onClick={handleSubmit(onSubmit)} disabled={updateGovernmentAllocationDistributionIsPending}>
                Salvar
              </Button>
            </div>
          </FormProvider>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default GovernmentAllocationDistributionUpdateModal;
